import React from 'react';

import { useShowInProgress } from '../../../../hooks/useShowInProgress';
import { BRAND_DEPLOYMENT_STRIP } from '../../../../theme/tokens';
import { slotTransform } from './stripSlot';

const { TILTS } = BRAND_DEPLOYMENT_STRIP;

type Options = {
  /** The `overflow-x` scroll container (the ScrollStrip's Strip element). */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Rendered card count — re-choreographs when the strip grows/shrinks. */
  count: number;
  reduced: boolean;
  /** Off while the strip is collapsed (its container is unmounted) — re-attaches on expand. */
  enabled: boolean;
};

/**
 * Drives the strip's PREMIUM scroll-linked choreography: on every scroll frame it measures
 * each card's distance from the container centre and writes its transform ({@link slotTransform})
 * straight to the DOM — gap push, organic rotation, centre lift + scale — so the effect tracks
 * the scroll continuously (no discrete hover/focal state, no threshold jump on chevron paging).
 *
 * Transforms are applied imperatively (not via React props) so a scroll frame is one cheap
 * pass of style writes with no re-render, and the Slot carries NO CSS transform transition —
 * the smoothness comes from the native scroll itself, frame-by-frame.
 *
 * Reads are batched into a `requestAnimationFrame` so multiple scroll events coalesce to one
 * paint per frame. Under reduced motion the choreography is OFF: cards sit flat in scroll
 * order (native scrolling still works), so nothing animates off the scroll.
 *
 * Cards are held UPRIGHT by default (no resting tilt, no lean); the organic tilt/lean is a
 * build-ahead style revealed by the `show_in_progress` flag. The gap, lift and scale are the
 * same either way. Reactive: flipping the flag repaints immediately.
 */
export const useStripChoreography = ({ containerRef, count, reduced, enabled }: Options): void => {
  const rafRef = React.useRef<number | undefined>(undefined);
  // Upright by default; the organic tilt/lean is revealed only under `show_in_progress`.
  const straight = !useShowInProgress();

  const paint = React.useCallback((): void => {
    const el = containerRef.current;
    if (!el) return;
    const kids = el.children;
    const len = kids.length;
    if (len === 0) return;
    const centre = el.scrollLeft + el.clientWidth / 2;
    // Step between adjacent card centres — normalises the per-card distance falloff.
    const first = kids[0] as HTMLElement;
    const second = kids[1] as HTMLElement | undefined;
    const step = second ? Math.abs(second.offsetLeft - first.offsetLeft) : first.offsetWidth;
    // READ pass first (measure every card's distance from centre), THEN a WRITE pass — never
    // interleave reads and writes, so applying a transform can't force a synchronous relayout
    // of the next measurement (no layout thrash → steady 60fps).
    const deltas = new Array<number>(len);
    for (let i = 0; i < len; i += 1) {
      const child = kids[i];
      deltas[i] = child instanceof HTMLElement ? child.offsetLeft + child.offsetWidth / 2 - centre : 0;
    }
    for (let i = 0; i < len; i += 1) {
      const child = kids[i];
      if (!(child instanceof HTMLElement)) continue;
      const { transform, z } = slotTransform(deltas[i], step, TILTS[i % TILTS.length], straight);
      child.style.transform = transform;
      child.style.zIndex = String(z);
    }
  }, [containerRef, straight]);

  const schedule = React.useCallback((): void => {
    if (rafRef.current !== undefined) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = undefined;
      paint();
    });
  }, [paint]);

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return undefined;

    if (reduced) {
      // Calm: no scroll-linked motion. Cards sit upright by default; when the tilt style is
      // revealed (flag on) they keep only their static scattered tilt. Nothing responds to the
      // scroll either way.
      for (let i = 0; i < el.children.length; i += 1) {
        const child = el.children[i];
        if (child instanceof HTMLElement) {
          child.style.transform = straight ? '' : `rotate(${TILTS[i % TILTS.length]}deg)`;
          child.style.zIndex = String(i);
        }
      }
      return undefined;
    }

    // Paint once now (in a layout effect, before the browser paints) so cards mount already
    // choreographed — no flash of the untransformed row. Runs AFTER useStripScroll's own layout
    // effect (called first in the parent), so the initial auto-follow scrollLeft is already set.
    paint();

    const onScroll = (): void => schedule();
    el.addEventListener('scroll', onScroll, { passive: true });
    const observer = new ResizeObserver(() => schedule());
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', onScroll);
      observer.disconnect();
      if (rafRef.current !== undefined) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = undefined;
      }
    };
  }, [containerRef, paint, schedule, count, reduced, straight, enabled]);
};

export default useStripChoreography;
